import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface MinimaxI2v01Params {
  /**
   * 输入图像URL或Base64编码
   */
  first_frame_image: string;
  /**
   * 视频生成提示词（可选），长度必须在0-2000个字符之间
   */
  prompt?: string;
  /**
   * 视频时长（秒），I2V-01固定为6秒
   * @default 6
   * @values 6
   */
  duration?: 6;
  /**
   * 视频分辨率，I2V-01固定为720P
   * @default "720P"
   * @values 720P
   */
  resolution?: '720P';
  /**
   * 是否启用提示词优化器，默认为true
   * @default true
   */
  prompt_optimizer?: boolean;
}

export interface MinimaxI2v01Resource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * minimax_i2v_01
 *
 * 通过MiniMax I2V-01 API创建图像到视频生成任务。
 *
 * @example
 * ```typescript
 * import { initSeacloud, minimaxI2v01 } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await minimaxI2v01({
 *   // 参数见 MinimaxI2v01Params 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 MinimaxI2v01Params 接口定义
 * @returns 生成的资源列表
 */
export async function minimaxI2v01(
  params: MinimaxI2v01Params,
): Promise<MinimaxI2v01Resource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 5000,
    maxAttempts: 120,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'minimax_i2v_01',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: MinimaxI2v01Resource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
